<template>
  <div v-if="shouldAsk" class="cookie" role="dialog" aria-modal="false" aria-labelledby="cookie-title">
    <div class="cookie__body">
      <div class="cookie__wrapper">
        <div class="cookie__content">
          <div id="cookie-title" class="cookie__title">
            {{ $t('cookie.title') }}
          </div>

          <div class="cookie__text cookie-text">
            <p v-if="!isSettingsOpen">
              {{ $t('cookie.description') }}
              <a href="/privacy" target="_blank">{{ $t('cookie.link') }}</a>
            </p>

            <div v-else class="cookie-text__checkbox">
              <div class="cookie-text__checkbox-group">
                <input id="cookie-necessary" type="checkbox" checked disabled />
                <label for="cookie-necessary">
                  {{ $t('cookie.necessary') }}
                  <span class="cookie-text__hint">{{ $t('cookie.necessary_hint') }}</span>
                </label>
              </div>

              <div class="cookie-text__checkbox-group">
                <input id="cookie-analytics" v-model="analyticsChoice" type="checkbox" />
                <label for="cookie-analytics">
                  {{ $t('cookie.analytics') }}
                  <span class="cookie-text__hint">{{ $t('cookie.analytics_hint') }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="cookie__button">
          <UiButtonMain
            role="button"
            :aria-label="$t('cookie.btn_accept_all')"
            size="md"
            block
            :label="$t('cookie.btn_accept_all')"
            @click="acceptAll"
          />

          <!-- Отказ равнозначен согласию и по виду, и по числу кликов: кнопка «принять»
               в один клик против отказа, спрятанного в настройках, — это то же самое
               отсутствие выбора, только оформленное аккуратнее. -->
          <UiButtonMain
            v-if="!isSettingsOpen"
            role="button"
            :aria-label="$t('cookie.btn_reject_all')"
            size="md"
            block
            :label="$t('cookie.btn_reject_all')"
            @click="rejectAll"
          />
          <UiButtonMain
            v-else
            role="button"
            :aria-label="$t('cookie.btn_save')"
            size="md"
            block
            :label="$t('cookie.btn_save')"
            @click="saveChoice"
          />

          <UiButtonMain
            v-if="!isSettingsOpen"
            role="button"
            :aria-label="$t('cookie.btn_settings')"
            size="md"
            block
            :label="$t('cookie.btn_settings')"
            @click="isSettingsOpen = true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useCookieConsent } from '@shared/lib';

/**
 * Баннер согласия на cookie.
 *
 * Крестика «закрыть» здесь намеренно нет. Раньше он — как и обе кнопки, и любой
 * выбор в чекбоксах — вызывал один и тот же `closeCookie()`, который просто прятал
 * баннер: выбор был нарисован, но никуда не сохранялся, а Метрика грузилась
 * до него в любом случае. Закрытие без ответа неотличимо от согласия, поэтому
 * баннер уходит только вместе с решением — «принять» или «только необходимые».
 */
const { shouldAsk, acceptAll, rejectAll, save } = useCookieConsent();

const isSettingsOpen = ref(false);

// Аналитика по умолчанию выключена: заранее отмеченная галочка согласием не считается.
const analyticsChoice = ref(false);

const saveChoice = () => save({ analytics: analyticsChoice.value, marketing: false });
</script>

<style scoped lang="scss">
.cookie {
  @apply fixed bottom-11 flex justify-center w-full px-4 z-10;

  &__body {
    @apply bg-white dark:bg-zinc-800 rounded-2xl px-4 md:px-16 py-4 md:py-11 max-w-[938px] border border-sky-200  shadow-[0_0px_24px_0px_rgba(0,0,0,0.08)] relative;
  }

  &__wrapper {
    @apply grid gap-4 md:grid-cols-[1fr_240px] md:gap-20;
  }

  &__content {
    @apply grid gap-4;
  }

  &__title {
    @apply dark:text-white;
  }

  &-text {
    p {
      @apply text-sm dark:text-white;
    }
    a {
      @apply text-sky-500 underline;
    }

    &__checkbox {
      @apply grid gap-3;

      &-group {
        @apply flex gap-2 dark:text-white items-start;

        input {
          @apply mt-1 shrink-0;
        }

        label {
          @apply text-sm cursor-pointer;
        }
      }
    }

    &__hint {
      @apply block text-xs opacity-70;
    }
  }

  &__button {
    @apply grid gap-2 items-center content-start;
  }
}
</style>
